import { Observable, MonoTypeOperatorFunction, throwError, of } from 'rxjs';

import { OnDestroy } from '@angular/core';
import { repeatWhen, delay, takeUntil, retryWhen, take, concatMap, tap, finalize, publishReplay, refCount, } from 'rxjs/operators';

export function getComponentOnDestroy$(cmp: OnDestroy): Observable<void> {
	return Observable.create(observer => {
		const ngOnDestroy = cmp.ngOnDestroy;
		cmp.ngOnDestroy = () => {
			observer.next(cmp);
			observer.complete();
			ngOnDestroy.apply(cmp);
		}
	})
}

export function takeUntilDestroyed<T>(cmp: OnDestroy): MonoTypeOperatorFunction<T> {
	return input$ => input$.pipe(
		takeUntil(getComponentOnDestroy$(cmp))
	);
}

export function repeatUntilDestroyed<T>(cmp: OnDestroy, timeInterval: number): MonoTypeOperatorFunction<T> {
	return input$ => input$.pipe(
		repeatWhen(completed => completed
			.pipe(
				delay(timeInterval)
			)
		),
		takeUntilDestroyed(cmp)
	);
}

export function autoSubscribe<T>(): MonoTypeOperatorFunction<T> {
	return input$ => {
		const obs = Observable.create(observer => {
			input$.subscribe(
				result => {
					observer.next(result);
					observer.complete();
				},
				error => {
					observer.error(error)
				})
		})

		obs.subscribe()
		return obs
	};
}

export function retryOnError<T>(retries: number, delayTime: number): MonoTypeOperatorFunction<T> {
	let i = 0;

	return input$ => input$.pipe(
		retryWhen(errors => errors.pipe(
			delay(delayTime),
			take(retries),
			concatMap(error => ++i < retries ? of(error) : throwError(error))
		)),
	);
}

export function cacheResult<T>(cacheTime: number): MonoTypeOperatorFunction<T> {
	return input$ => input$.pipe(
		publishReplay(1, cacheTime),
		refCount(),
		take(1)
	);
}

export function tapOnError<T>(errorFn: (e: any) => void): MonoTypeOperatorFunction<T> {
	return input$ => input$.pipe(
		tap(() => {}, errorFn)
	);
}